import { useQuery, type QueryClient } from '@tanstack/react-query'

import { QuotaExceededError } from '@/lib/api-client'
import { fetchSerpQuotaStatus, parseQuotaStatus, SERP_QUOTA_QUERY_KEY, type FreeLimits, type SerpQuotaStatus } from '@/lib/serp-quota'

export interface UseSerpQuotaResult {
  status: SerpQuotaStatus | null
  isLoading: boolean
  /** Sınırlı kullanıcı mı (anahtar açık ve sınırsız değil). Yükleniyor/hata/kapalı iken false: FAIL-OPEN, gerçek kapı sunucudadır. */
  isLimited: boolean
  remaining: number | null
  limit: number | null
  resetsAt: string | null
  /** Sunucu sayısal Free sınırlarını yolladıysa; yoksa null. */
  freeLimits: FreeLimits | null
  /** Sınırlı ve kalan hak 0. */
  isExhausted: boolean
}

/** Bugünkü ürün hakkı durumu. Veri 30 sn taze sayılır, sekmeye dönünce yenilenir. */
export function useSerpQuota(): UseSerpQuotaResult {
  const query = useQuery({
    queryKey: SERP_QUOTA_QUERY_KEY,
    queryFn: fetchSerpQuotaStatus,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    retry: false,
  })
  const status = query.status === 'success' ? (query.data ?? null) : null
  const isLimited = status !== null && status.enabled && !status.unlimited
  const remaining = isLimited ? status.remaining : null
  return {
    status,
    isLoading: query.isLoading,
    isLimited,
    remaining,
    limit: isLimited ? status.limit : null,
    resetsAt: isLimited ? status.resetsAt : null,
    freeLimits: isLimited ? status.freeLimits : null,
    isExhausted: isLimited && remaining !== null && remaining <= 0,
  }
}

/**
 * 429 `QuotaExceededError` yakalanınca çağrılır: önbelleği `remaining: 0` yapar.
 * Başka hata türünde hiçbir şey yapmaz; true dönerse hata kota aşımıydı.
 */
export function noteQuotaExceeded(queryClient: QueryClient, err: unknown): boolean {
  if (!(err instanceof QuotaExceededError)) return false
  queryClient.setQueryData<SerpQuotaStatus | null>(SERP_QUOTA_QUERY_KEY, (prev) => {
    const limit = err.limit ?? prev?.limit ?? 0
    return {
      enabled: true,
      unlimited: false,
      limit,
      used: limit,
      remaining: 0,
      resetsAt: err.resetsAt ?? prev?.resetsAt ?? null,
      freeLimits: prev?.freeLimits ?? null,
    }
  })
  return true
}

/**
 * Yanıtın `meta.quota` alanı (sunucu limited modda her ürün sayımında günceli yollar) önbelleğe yazılır:
 * sayaç ek istek atmadan güncel kalır. Alan yok/bozuksa hiçbir şey yapmaz (kapalıyken sessiz).
 */
export function noteQuotaMeta(queryClient: QueryClient, meta: unknown): boolean {
  if (!meta || typeof meta !== 'object') return false
  const parsed = parseQuotaStatus((meta as { quota?: unknown }).quota)
  if (!parsed) return false
  queryClient.setQueryData<SerpQuotaStatus | null>(SERP_QUOTA_QUERY_KEY, (prev) => ({
    ...parsed,
    freeLimits: parsed.freeLimits ?? prev?.freeLimits ?? null,
  }))
  return true
}
